import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, CheckCircle2, Clock, Code2,
  Crown, Flame, Lock, Menu, Sparkles,
  Star, Trophy, X, Zap
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { MazeGame, MazeComplete, MazeLeaderboard } from './NeonMaze';
import ReflexTapGame from './ReflexTapGame';
import ReflexLeaderboard from './ReflexLeaderboard';
import { submitArenaResult, getArenaStats, getTodayStatus } from '../services/arenaService';

// Guards / wrappers used by this component
function SwipeCards({ games }) {
  const trackRef    = React.useRef(null);
  const startX      = React.useRef(0);
  const startY      = React.useRef(0);
  const currentX    = React.useRef(0);
  const dragging    = React.useRef(false);
  const lockedAxis  = React.useRef(null); // 'h' | 'v' | null
  const [index, setIndex] = React.useState(0);
  const indexRef    = React.useRef(0);
  const countRef    = React.useRef(games?.length ?? 0);

  React.useEffect(() => {
    indexRef.current = 0;
    countRef.current = games?.length ?? 0;
    setIndex(0);
  }, [games?.length]);

  React.useEffect(() => { indexRef.current = index; }, [index]);

  React.useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    function clamp(n) {
      return Math.max(0, Math.min(countRef.current - 1, n));
    }

    function setTranslate(offsetPx, animate) {
      const base = -(indexRef.current * 100);
      const pct = el.parentElement ? (offsetPx / el.parentElement.offsetWidth) * 100 : 0;
      el.style.transition = animate ? 'transform 0.28s ease' : 'none';
      el.style.transform = `translateX(calc(${base}% + ${offsetPx}px))`;
    }

    function onTouchStart(e) {
      if (e.touches.length !== 1) return;
      startX.current = e.touches[0].clientX;
      startY.current = e.touches[0].clientY;
      currentX.current = 0;
      dragging.current = true;
      lockedAxis.current = null;
      el.style.transition = 'none';
    }

    function onTouchMove(e) {
      if (!dragging.current) return;
      const dx = e.touches[0].clientX - startX.current;
      const dy = e.touches[0].clientY - startY.current;

      // Determine axis lock on first significant movement
      if (!lockedAxis.current) {
        if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
        lockedAxis.current = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
      }

      if (lockedAxis.current === 'v') return; // let page scroll

      // Horizontal — prevent page scroll and apply live drag
      e.preventDefault();
      currentX.current = dx;
      setTranslate(dx, false);
    }

    function onTouchEnd() {
      if (!dragging.current) return;
      dragging.current = false;

      if (lockedAxis.current !== 'h') {
        lockedAxis.current = null;
        return;
      }

      lockedAxis.current = null;
      const dx = currentX.current;
      const THRESHOLD = (el.parentElement?.offsetWidth ?? 120) * 0.25;

      let next = indexRef.current;
      if (dx < -THRESHOLD) next = clamp(indexRef.current + 1);
      else if (dx > THRESHOLD) next = clamp(indexRef.current - 1);

      indexRef.current = next;
      setIndex(next);
      // Snap to final position with animation
      el.style.transition = 'transform 0.28s ease';
      el.style.transform = `translateX(${-(next * 100)}%)`;
    }

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd);

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, []);

  if (!games?.length) return null;

  return (
    <div className="sm:hidden mt-4">
      {/* overflow wrapper */}
      <div className="overflow-hidden rounded-2xl" style={{ touchAction: 'pan-y' }}>
        <div
          ref={trackRef}
          className="flex will-change-transform"
          style={{ transform: `translateX(-${index * 100}%)`, transition: 'transform 0.28s ease' }}
        >
          {games.map((g) => (
            <div key={g.title} className="min-w-full px-0.5">
              <GameCard game={g} />
            </div>
          ))}
        </div>
      </div>

      {/* dots */}
      <div className="mt-2 flex items-center justify-center gap-2">
        {games.map((g, i) => (
          <button
            key={g.title}
            type="button"
            aria-label={`Go to ${g.title}`}
            onClick={() => {
              indexRef.current = i;
              setIndex(i);
              if (trackRef.current) {
                trackRef.current.style.transition = 'transform 0.28s ease';
                trackRef.current.style.transform = `translateX(-${i * 100}%)`;
              }
            }}
            className={`h-1.5 w-6 rounded-full transition-all ${i === index ? 'bg-purple-600 w-8' : 'bg-slate-200'}`}
          />
        ))}
      </div>
    </div>
  );
}

// Guards / wrappers used by this component
function NeonMazeTodayGuard({ children, onPlayed /* (result) => void */ }) {
  // Current implementation: since this arena is already authenticated-only,
  // we simply render the game.
  return children;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const VIEWS = {
  HOME: 'home',
  MAZE_PLAY: 'maze_play', MAZE_COMPLETE: 'maze_complete', MAZE_LEADERBOARD: 'maze_leaderboard',
  REFLEX_PLAY: 'reflex_play', REFLEX_LEADERBOARD: 'reflex_leaderboard',
};

// ─── Per-game data ────────────────────────────────────────────────────────────

const MAZE_STATS_DEFAULT = { currentStreak: 0, bestRank: null, totalCompleted: 0, bestTime: null };

const MAZE_ACHIEVEMENTS = [
  { id: 's1',  icon: Star,   label: '1 Day Streak',  color: 'from-amber-400 to-yellow-500',  streak: 1  },
  { id: 's7',  icon: Flame,  label: '7 Day Streak',  color: 'from-orange-400 to-red-500',    streak: 7  },
  { id: 's30', icon: Zap,    label: '30 Day Streak', color: 'from-purple-500 to-violet-600', streak: 30 },
  { id: 's50', icon: Crown,  label: '50 Day Streak', color: 'from-amber-500 to-orange-600',  streak: 50 },
  { id: 's100',icon: Trophy, label: '100 Day Streak',color: 'from-emerald-400 to-teal-500',  streak: 100},
];

const REFLEX_STATS_DEFAULT = {
  currentStreak: 0,
  bestRank: null,
  totalCompleted: 0,
  bestTime: null,
  bestScore: null,
  bestAvgReactionTime: null,
};

const REFLEX_ACHIEVEMENTS = [
  { id: 's1',  icon: Star,   label: '1 Day Streak',  color: 'from-amber-400 to-yellow-500',  streak: 1  },
  { id: 's7',  icon: Flame,  label: '7 Day Streak',  color: 'from-orange-400 to-red-500',    streak: 7  },
  { id: 's30', icon: Zap,    label: '30 Day Streak', color: 'from-purple-500 to-violet-600', streak: 30 },
  { id: 's50', icon: Crown,  label: '50 Day Streak', color: 'from-amber-500 to-orange-600',  streak: 50 },
  { id: 's100',icon: Trophy, label: '100 Day Streak',color: 'from-emerald-400 to-teal-500',  streak: 100},
];

// ─── Streak value helper (icon + text, no emoji) ────────────────────────────
function StreakValue({ count }) {
  if (!count) return '—';
  return (
    <span className="inline-flex items-center gap-0.5">
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-orange-500">
        <path d="M12 2s4 4 4 8a4 4 0 0 1-8 0c0-4 4-8 4-8Z" />
        <path d="M7 14c0 4 2 8 5 8s5-4 5-8" />
      </svg>
      {count}d
    </span>
  );
}

function getTimeUntilMidnight() {
  const now = new Date(), midnight = new Date(now);
  midnight.setHours(24, 0, 0, 0);
  const diff = midnight - now;
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
}

function fmt(s) {
  if (!s && s !== 0) return '—';
  if (s < 60) return `${s}s`;
  return `${Math.floor(s/60)}m ${s%60}s`;
}

// ─── Shared badge component ───────────────────────────────────────────────────

function Badge({ badge, currentStreak }) {
  const unlocked = (currentStreak ?? 0) >= badge.streak;
  return (
    <div className={`relative flex flex-col items-center gap-0.5 ${!unlocked ? 'opacity-35 grayscale' : ''}`}>
      <div className={`flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br ${badge.color} shadow-sm`}>
        <badge.icon className="h-4 w-4 text-white" />
      </div>
      {!unlocked && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Lock className="h-3 w-3 text-slate-500" />
        </div>
      )}
      <span className="text-center text-[8px] font-semibold leading-tight text-slate-400 max-w-[36px]">{badge.label}</span>
    </div>
  );
}

// ─── Neon Maze Preview (SVG mockup) ──────────────────────────────────────────

function NeonMazePreview() {
  return (
    <div className="relative h-36 w-full overflow-hidden rounded-t-2xl" style={{ background: '#0f0f1a' }}>
      <div className="absolute inset-0" style={{ background: 'radial-gradient(circle at 20% 20%, rgba(124,58,237,0.3), transparent 50%), radial-gradient(circle at 80% 80%, rgba(249,115,22,0.2), transparent 50%)' }} />
      <svg width="100%" height="100%" viewBox="0 0 280 140" preserveAspectRatio="xMidYMid slice">
        <defs>
          <filter id="pg"><feGaussianBlur stdDeviation="2" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
          <radialGradient id="pg2"><stop offset="0%" stopColor="#fed7aa"/><stop offset="100%" stopColor="#f97316"/></radialGradient>
        </defs>
        {/* maze walls */}
        {[
          'M20,20 L260,20','M20,20 L20,120','M260,20 L260,120','M20,120 L260,120',
          'M20,50 L80,50','M110,50 L170,50','M200,50 L260,50',
          'M20,80 L50,80','M80,80 L140,80','M170,80 L230,80',
          'M50,20 L50,50','M80,50 L80,80','M110,20 L110,50',
          'M140,50 L140,80','M170,20 L170,80','M200,80 L200,120',
          'M230,50 L230,80','M50,80 L50,120','M110,80 L110,120',
        ].map((d,i) => <path key={i} d={d} stroke="#7c3aed" strokeWidth="1.5" fill="none" filter="url(#pg)" opacity="0.8"/>)}
        {/* start */}
        <circle cx="35" cy="35" r="5" fill="rgba(139,92,246,0.4)" filter="url(#pg)"/>
        <text x="35" y="39" textAnchor="middle" fontSize="7" fill="rgba(139,92,246,0.8)">S</text>
        {/* finish glow */}
        <rect x="245" y="106" width="14" height="14" fill="rgba(249,115,22,0.25)" rx="2"/>
        <text x="252" y="117" textAnchor="middle" fontSize="7" fill="rgba(249,115,22,1)">F</text>
        {/* player */}
        <circle cx="80" cy="35" r="6" fill="url(#pg2)" filter="url(#pg)"/>
      </svg>
      <div className="absolute bottom-2 left-3 flex items-center gap-1.5">
        <span className="rounded-full bg-purple-900/70 px-2 py-0.5 text-[10px] font-bold text-purple-300 backdrop-blur">Navigation</span>
        <span className="rounded-full bg-amber-900/70 px-2 py-0.5 text-[10px] font-bold text-amber-300 backdrop-blur">Medium</span>
      </div>
    </div>
  );
}

// ─── Reflex Tap Preview (SVG mockup) ─────────────────────────────────────────

function ReflexTapPreview() {
  return (
    <div className="relative h-36 w-full overflow-hidden rounded-t-2xl" style={{ background: '#0a0a1a' }}>
      <div className="absolute inset-0" style={{ background: 'radial-gradient(circle at 50% 50%, rgba(14,165,233,0.25), transparent 60%)' }} />
      <svg width="100%" height="100%" viewBox="0 0 280 140" preserveAspectRatio="xMidYMid slice">
        <defs>
          <filter id="rf"><feGaussianBlur stdDeviation="3" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
        </defs>
        {/* outer ring */}
        <circle cx="140" cy="70" r="52" stroke="#0ea5e9" strokeWidth="1.5" fill="none" opacity="0.4" filter="url(#rf)"/>
        <circle cx="140" cy="70" r="38" stroke="#0ea5e9" strokeWidth="1" fill="none" opacity="0.3"/>
        <circle cx="140" cy="70" r="24" stroke="#38bdf8" strokeWidth="1.5" fill="rgba(14,165,233,0.1)" filter="url(#rf)"/>
        {/* tap target */}
        <circle cx="140" cy="70" r="14" fill="rgba(14,165,233,0.9)" filter="url(#rf)"/>
        <text x="140" y="75" textAnchor="middle" fontSize="10" fontWeight="bold" fill="white">TAP</text>
        {/* reaction time indicator */}
        <rect x="50" y="112" width="180" height="6" rx="3" fill="rgba(255,255,255,0.08)"/>
        <rect x="50" y="112" width="110" height="6" rx="3" fill="rgba(14,165,233,0.7)" filter="url(#rf)"/>
        {/* score pings */}
        {[[60,45],[210,55],[90,90],[190,85]].map(([x,y],i) => (
          <circle key={i} cx={x} cy={y} r="3" fill="rgba(56,189,248,0.5)" filter="url(#rf)"/>
        ))}
      </svg>
      <div className="absolute bottom-2 left-3 flex items-center gap-1.5">
        <span className="rounded-full bg-sky-900/70 px-2 py-0.5 text-[10px] font-bold text-sky-300 backdrop-blur">Reaction Speed</span>
        <span className="rounded-full bg-emerald-900/70 px-2 py-0.5 text-[10px] font-bold text-emerald-300 backdrop-blur">Easy</span>
      </div>
    </div>
  );
}

// ─── Game Card ────────────────────────────────────────────────────────────────

function GameCard({ game }) {
  const isComingSoon = game.comingSoon;
  return (
    <div className={`flex flex-col overflow-hidden rounded-2xl border bg-white shadow-sm transition ${isComingSoon ? 'border-slate-200 opacity-90' : 'border-slate-200 hover:border-purple-200 hover:shadow-md'}`}>
      {/* Banner */}
      {game.preview}

      {/* Body */}
      <div className="flex flex-1 flex-col p-3">
        <div className="mb-2 flex items-start justify-between gap-1">
          <div className="min-w-0">
            <h3 className="truncate text-sm font-extrabold text-slate-900">{game.title}</h3>
            <p className="mt-0.5 text-[10px] leading-4 text-slate-500 line-clamp-2">{game.description}</p>
          </div>
          {game.completed && (
            <div className="shrink-0 flex items-center gap-0.5 rounded-lg bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700">
              <CheckCircle2 className="h-3 w-3" /> Done
            </div>
          )}
        </div>

        {/* Stats grid */}
        <div className="mb-2 grid grid-cols-2 gap-1">
          {game.stats.map(s => (
            <div key={s.label} className="rounded-lg bg-slate-50 px-2 py-1.5 text-center">
              <div className="text-xs font-extrabold text-slate-900 flex items-center justify-center gap-0.5">{s.node ?? s.value}</div>
              <div className="text-[9px] font-semibold text-slate-400 leading-tight">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Achievements */}
        <div className="mb-3 flex items-center justify-between gap-1">
          {game.achievements.map(a => <Badge key={a.id} badge={a} currentStreak={game.currentStreak} />)}
        </div>

        {/* CTA */}
        <div className="mt-auto">
          {isComingSoon ? (
            <div className="flex w-full items-center justify-center gap-1.5 rounded-xl bg-slate-100 py-2 text-xs font-bold text-slate-400 cursor-not-allowed">
              <Clock className="h-3.5 w-3.5" /> Coming Soon
            </div>
          ) : (
            <button
              disabled={game.completed}
              onClick={game.completed ? undefined : game.onPlay}
              className="flex w-full items-center justify-center gap-1.5 rounded-xl bg-gradient-to-r from-purple-600 to-orange-500 py-2 text-xs font-bold text-white shadow-md shadow-purple-200 transition hover:brightness-105 disabled:opacity-60 disabled:cursor-not-allowed"
            >
          {game.completed
                ? <><CheckCircle2 className="h-3.5 w-3.5" /> Completed (Today)</>
                : <>{game.ctaLabel} <ArrowRight className="h-3.5 w-3.5" /></>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Arena Dashboard ──────────────────────────────────────────────────────────

function ArenaDashboard({ onPlayMaze, onPlayReflex, mazeCompleted, reflexCompleted, mazeStats, reflexStats, onViewNeonMazeLeaderboard, onViewReflexLeaderboard }) {
  const { isAuthenticated } = useAuth();

  const [countdown, setCountdown] = React.useState(getTimeUntilMidnight());

  React.useEffect(() => {
    const t = setInterval(() => setCountdown(getTimeUntilMidnight()), 1000);
    return () => clearInterval(t);
  }, []);

  const games = [
    {
      title: 'Neon Maze',
      description: 'Navigate the daily maze. Rank by completion time. Fastest solver wins.',
      preview: <NeonMazePreview />,
      completed: mazeCompleted,
      comingSoon: false,
      ctaLabel: 'Play Neon Maze',
      onPlay: onPlayMaze,
      currentStreak: mazeStats.currentStreak ?? 0,
      stats: [
        { label: 'Streak',    node: <StreakValue count={mazeStats.currentStreak} />, value: mazeStats.currentStreak ? `${mazeStats.currentStreak}d` : '—' },
        { label: 'Best Rank', value: mazeStats.bestRank ? `#${mazeStats.bestRank}` : '—'             },
        { label: 'Completed', value: mazeStats.totalCompleted ?? '—'                                  },
        { label: 'Best Time', value: fmt(mazeStats.bestTime)                                          },
      ],
      achievements: MAZE_ACHIEVEMENTS,
    },
    {
      title: 'Reflex Tap',
      description: 'Test your reaction speed. Tap the target as fast as you can.',
      preview: <ReflexTapPreview />,
      completed: reflexCompleted,
      comingSoon: false,
      ctaLabel: 'Play Reflex Tap',
      onPlay: onPlayReflex,
      currentStreak: reflexStats.currentStreak ?? 0,
      stats: [
        { label: 'Streak',    node: <StreakValue count={reflexStats.currentStreak} />, value: reflexStats.currentStreak ? `${reflexStats.currentStreak}d` : '—' },
        { label: 'Best Rank', value: reflexStats.bestRank ? `#${reflexStats.bestRank}` : '—'               },
        { label: 'Completed', value: reflexStats.totalCompleted ?? '—'                                     },
        { label: 'Best Score', value: reflexStats.bestScore != null ? reflexStats.bestScore : '—'     },
        { label: 'Best RT', value: reflexStats.bestAvgReactionTime != null ? `${reflexStats.bestAvgReactionTime}ms` : '—' },
      ],
      achievements: REFLEX_ACHIEVEMENTS,
    },
  ];

  return (
    <div className="mx-auto max-w-2xl px-4 py-5">

      {/* Welcome banner */}
      <div className="mb-5 overflow-hidden rounded-2xl border border-white/80 bg-white shadow-sm">
        <div className="relative overflow-hidden p-5">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(124,58,237,0.1),transparent_50%),radial-gradient(circle_at_bottom_right,rgba(249,115,22,0.08),transparent_50%)]" />
          <div className="relative flex items-start justify-between gap-4">
            <div>
              <div className="mb-1 text-[11px] font-bold uppercase tracking-widest text-purple-600">Daily Challenge Arena</div>
              <h2 className="text-xl font-extrabold text-slate-900">
                {mazeCompleted ? "You've crushed today's maze!" : "Ready for today's challenges?"}
              </h2>
              <p className="mt-1.5 text-xs leading-5 text-slate-600">
                New challenges drop every day. Rank on the global leaderboard.
              </p>
            </div>
            <div className="shrink-0 text-right">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Next in</div>
              <div className="font-mono text-sm font-bold text-purple-700">{countdown}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Game cards */}
      {/* Desktop/tablet: 2-column grid. Mobile: horizontal swipe */}
      <div className="hidden gap-4 sm:grid sm:grid-cols-2">
        {games.map(g => <GameCard key={g.title} game={g} />)}
      </div>

      <SwipeCards games={games} />

      {/* Leaderboard buttons */}
      <div className="mt-4 flex flex-col gap-2">
        <button
          onClick={onViewNeonMazeLeaderboard}
          className="w-full flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-purple-600 to-orange-500 py-2.5 text-sm font-bold text-white shadow-lg shadow-purple-900/40 transition hover:brightness-110"
        >
          <Trophy className="h-4 w-4" />
          View Neon Maze Leaderboard
        </button>
        <button
          onClick={onViewReflexLeaderboard}
          className="w-full flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-sky-500 to-blue-600 py-2.5 text-sm font-bold text-white shadow-lg shadow-sky-900/40 transition hover:brightness-110"
        >
          <Zap className="h-4 w-4" />
          View Reflex Tap Leaderboard
        </button>
      </div>



      {/* Guest CTA */}
      {!isAuthenticated && (
        <div className="mt-5 rounded-2xl border border-purple-200 bg-gradient-to-br from-purple-50 to-orange-50 p-5 text-center">
          <div className="mb-1 text-sm font-bold text-slate-900">Save your streaks & ranks</div>
          <p className="mb-4 text-xs text-slate-600">Create a free account to track progress, earn badges, and appear on the leaderboard.</p>
          <Link to="/signup" className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-purple-600 to-orange-500 px-6 py-2.5 text-sm font-bold text-white shadow-lg shadow-purple-200">
            Create Free Account <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function DailyChallengeArena() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  const [view, setView] = React.useState(VIEWS.HOME);
  const [mazeResult, setMazeResult] = React.useState(null);
  const [mazeCompleted, setMazeCompleted] = React.useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [mazeStats, setMazeStats] = React.useState(MAZE_STATS_DEFAULT);
  const [reflexStats, setReflexStats] = React.useState(REFLEX_STATS_DEFAULT);
  const [reflexCompleted, setReflexCompleted] = React.useState(false);

  // Load stats + today-status on mount / whenever user logs in.
  // IMPORTANT: do not reset local completion/view state for guests,
  // otherwise guest submit can appear like a "reload".
  React.useEffect(() => {
    if (!isAuthenticated) return;

    Promise.all([
      getArenaStats('neon-maze').catch(() => MAZE_STATS_DEFAULT),
      getArenaStats('reflex-tap').catch(() => REFLEX_STATS_DEFAULT),
      getTodayStatus('neon-maze').catch(() => ({ played: false, result: null })),
      getTodayStatus('reflex-tap').catch(() => ({ played: false, result: null })),
    ]).then(([ms, rs, todayMaze, todayReflex]) => {
      setMazeStats(ms);
      setReflexStats(rs);
      setMazeCompleted(!!todayMaze.played);
      setReflexCompleted(!!todayReflex.played);
    });
  }, [isAuthenticated]);


  const handleMazeComplete = (res) => {
    setMazeResult(res);
    // Immediately reflect completion for the current user (prevents stale badge when login refreshed)
    setMazeCompleted(true);
    setView(VIEWS.MAZE_COMPLETE);

  // For guest: backend submission may be blocked.
    // Still show leaderboard UI immediately after completion, but don’t depend on today-status
    // to avoid falling back/redirecting due to auth refresh timing.

    if (!isAuthenticated) {
      setTimeout(() => {
        setView(VIEWS.MAZE_LEADERBOARD);
      }, 800);
      return;
    }

    // Refresh server state so leaderboard + badge stay consistent.
    Promise.all([
      getArenaStats('neon-maze').catch(() => MAZE_STATS_DEFAULT),
      getArenaStats('reflex-tap').catch(() => REFLEX_STATS_DEFAULT),
      getTodayStatus('neon-maze').catch(() => ({ played: false, result: null })),
      getTodayStatus('reflex-tap').catch(() => ({ played: false, result: null })),
    ]).then(([ms, rs, todayMaze, todayReflex]) => {
      setMazeStats(ms);
      setReflexStats(rs);

      // Only trust todayMaze.played for authenticated users.
      const shouldGo = isAuthenticated ? !!todayMaze.played : true;
      if (todayReflex.played) setReflexCompleted(true);

      if (shouldGo) {
        setMazeCompleted(true);

        // Short delay on completion screen
        setTimeout(() => {
          setView(VIEWS.MAZE_LEADERBOARD);
        }, 800);
      }
    });
  };


  // Full-screen game views
  if (view === VIEWS.MAZE_PLAY) {
    // Hard-block reattempt + guests.
    // If user already played today, do not allow entering the play screen again.
    if (!isAuthenticated) {
      // Guest can PLAY, but must NOT save anything to DB.
      // Backend will reject /api/arena/submit anyway; we also skip calling it in the completion handler below.
    }


    // Re-check today-status and block reattempt.
    // NOTE: We keep it simple: if already played, show completion screen.
    // If not played, show the game.
    return <NeonMazeTodayGuard
      gameType="neon-maze"
      onPlayed={(result) => {
        setMazeResult(result);
        setMazeCompleted(true);

        // If guest: don't prompt login, just show result locally.
        // (Backend save is already blocked by /api/arena/submit requiring auth.)
        setView(VIEWS.MAZE_COMPLETE);
      }}

      onReady={() => {}}
    >
      <MazeGame
        difficulty="Medium"
        streak={mazeStats.currentStreak}
        bestRank={mazeStats.bestRank}
        onComplete={handleMazeComplete}
        onBack={() => setView(VIEWS.HOME)}
      />
    </NeonMazeTodayGuard>;

  }

  if (view === VIEWS.MAZE_COMPLETE && mazeResult) {
    return (
      <MazeComplete
        result={mazeResult}
        streak={mazeStats.currentStreak}
        personalBest={mazeStats.bestTime}
        onViewLeaderboard={() => setView(VIEWS.MAZE_LEADERBOARD)}
        onGoArena={() => setView(VIEWS.HOME)}
      />
    );
  }
  if (view === VIEWS.MAZE_LEADERBOARD) {
    return <MazeLeaderboard onBack={() => setView(VIEWS.HOME)} />;
  }
  if (view === VIEWS.REFLEX_PLAY) {
    return (
      <ReflexTapGame
        personalBestScore={reflexStats.bestScore}
        onBack={() => setView(VIEWS.HOME)}
        onComplete={async (stats) => {
          if (isAuthenticated) {
            try {
              await submitArenaResult({
                gameType: 'reflex-tap',
                completionTime: stats.completionTime,
                score: stats.score,
                totalTaps: stats.totalTaps,
                averageReactionTime: stats.averageReactionTime,
                maxCombo: stats.maxCombo,
              });
            } catch (_) {}

            Promise.all([
              getArenaStats('reflex-tap').catch(() => REFLEX_STATS_DEFAULT),
            ]).then(([rs]) => setReflexStats(rs));
          }

          setReflexCompleted(true);
          setView(VIEWS.REFLEX_LEADERBOARD);
        }}
      />
    );
  }

  if (view === VIEWS.REFLEX_LEADERBOARD) {
    return <ReflexLeaderboard onBack={() => setView(VIEWS.HOME)} currentUserUid={user?.uid} />;
  }

  // Arena shell
  return (
    <div className="min-h-screen" style={{ background: 'radial-gradient(circle at 10% 0%, rgba(124,58,237,0.1), transparent 30%), radial-gradient(circle at 90% 5%, rgba(249,115,22,0.08), transparent 25%), #f8fafc' }}>

      {/* Mobile drawer backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/50 transition-opacity xl:hidden ${mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={() => setMobileMenuOpen(false)}
      />
      {/* Mobile drawer */}
      <div className={`fixed inset-y-0 left-0 z-50 w-72 overflow-y-auto bg-white shadow-2xl transition-transform duration-300 xl:hidden ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex items-center justify-between border-b border-slate-200 p-4">
          <span className="font-bold text-slate-900">Daily Challenge Arena</span>
          <button onClick={() => setMobileMenuOpen(false)} className="rounded-lg p-2 hover:bg-slate-100">
            <X className="h-5 w-5 text-slate-700" />
          </button>
        </div>
        <nav className="p-4 space-y-1">
          <button onClick={() => { setView(VIEWS.HOME); setMobileMenuOpen(false); }}
            className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold bg-purple-50 text-purple-700">
            <Sparkles className="h-4 w-4" />Arena
          </button>
          <button onClick={() => { setView(VIEWS.MAZE_LEADERBOARD); setMobileMenuOpen(false); }}
            className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-100">
            <Trophy className="h-4 w-4" />Neon Maze Leaderboard
          </button>
          <button onClick={() => { setView(VIEWS.REFLEX_LEADERBOARD); setMobileMenuOpen(false); }}
            className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-100">
            <Zap className="h-4 w-4" />Reflex Tap Leaderboard
          </button>

          <div className="pt-3 border-t border-slate-100 mt-3">
            <button onClick={() => { navigate('/practice-arena'); setMobileMenuOpen(false); }}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-100">
              <Code2 className="h-4 w-4" />Practice Arena
            </button>
            <button onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-100">
              <ArrowLeft className="h-4 w-4" />Dashboard
            </button>
          </div>
        </nav>
      </div>

      {/* Top navbar */}
      <header className="sticky top-0 z-30 border-b border-slate-200/80 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-2xl items-center gap-3 px-4 py-3">
          <button onClick={() => setMobileMenuOpen(true)} className="flex h-9 w-9 items-center justify-center rounded-lg hover:bg-slate-100 xl:hidden">
            <Menu className="h-5 w-5 text-slate-700" />
          </button>
          <Link to="/dashboard" className="flex items-center gap-2 shrink-0">
            <img src="/favicon.png" alt="Logo" className="h-9 w-9 rounded-xl object-contain shadow-md shadow-purple-200" />
            <div className="hidden sm:block">
              <div className="text-sm font-extrabold leading-none text-slate-900">Daily Challenge</div>
              <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Arena</div>
            </div>
          </Link>

          <div className="ml-auto flex items-center gap-2">
            <Link
              to="/dashboard"
              className="hidden sm:flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
              Dashboard
            </Link>
            <div className="flex items-center gap-1.5 rounded-full border border-orange-100 bg-orange-50 px-3 py-1.5 text-xs font-bold text-orange-600">
              <Flame className="h-3.5 w-3.5" />{mazeStats.currentStreak || 0}d streak
            </div>
            {isAuthenticated && (
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-orange-400 text-xs font-bold text-white">
                {user?.displayName?.charAt(0) || 'U'}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Content */}
      <ArenaDashboard
        onPlayMaze={() => setView(VIEWS.MAZE_PLAY)}
        onPlayReflex={() => setView(VIEWS.REFLEX_PLAY)}
        onViewNeonMazeLeaderboard={() => setView(VIEWS.MAZE_LEADERBOARD)}
        onViewReflexLeaderboard={() => setView(VIEWS.REFLEX_LEADERBOARD)}
        mazeCompleted={mazeCompleted}
        reflexCompleted={reflexCompleted}
        mazeStats={mazeStats}
        reflexStats={reflexStats}
      />

    </div>
  );
}
